import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormGroupDirective, Validators } from "@angular/forms";
import { MatSidenav } from "@angular/material/sidenav";
import { MatSort } from "@angular/material/sort";
import { MatStep, MatStepper } from "@angular/material/stepper";
import { MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import { faDownload, faUpload } from "@fortawesome/free-solid-svg-icons";
import { TranslateService } from "@ngx-translate/core";
import { lastValueFrom, Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { StatusTypes, ViewType } from "src/app/models/Enums";
import { FileMetaData, FileToUpload } from "src/app/models/FileToUpload";
import { Tool } from "src/app/models/Tool";
import { ColumnFilter, ToolFilter } from "src/app/models/ToolFilter";
import { BrandingService } from "src/app/services/branding.service";
import { EventService } from "src/app/services/event.service";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { ModalService } from "src/app/services/modal.service";

import { ToolService } from "../../services/tool.service";
import { ConfirmationDialogData } from "../common/confirmation-dialog/confirmation-dialog-modal.component";
import { Address } from "./../../models/Address";
import { PoSubmit } from "./../../models/PoSubmit";
import { CompanyService } from "./../../services/company.service";
import { PoBasketService } from "./../../services/po-basket.service";
import { StorageService } from "./../../services/storage.service";

export enum ProcessType {
  Normal = 1,
  Scrap,
  ScrapAndQuote,
  Rur,
}

@Component({
  selector: "app-purchase-order",
  templateUrl: "./purchase-order.component.html",
  styleUrls: ["./purchase-order.component.scss"],
  standalone: false,
})
export class PurchaseOrderComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("poForm") poForm: FormGroupDirective;
  @ViewChild("sidePurchaseOrderStepper") sidePurchaseOrderStepper: MatSidenav;
  @ViewChild("stepEnterPOform") stepEnterPOform: FormGroupDirective;
  @ViewChild("stepShippingForm") stepShippingForm: FormGroupDirective;
  @ViewChild("stepper") stepper: MatStepper;

  readonly routeNameConstants = RouteNameConstants;
  readonly localizationConstants = LocalizationConstants;
  isSubmitPOPressed: boolean;
  private poUploadedFile: File;
  private _subscriptions$: Subscription[] = [];

  faUpload = faUpload;
  faDownload = faDownload;
  poResult: boolean;
  po: PoSubmit;
  currentShipTo: Address;
  shipToList: Address[];
  processType: ProcessType = ProcessType.Normal;
  processTypeList = [
    { key: ProcessType.Normal, title: "purchaseorder.normalprocess" },
    { key: ProcessType.Scrap, title: "purchaseorder.scrapprocess" },
    {
      key: ProcessType.ScrapAndQuote,
      title: "purchaseorder.scrapquoteprocess",
    },
    { key: ProcessType.Rur, title: "purchaseorder.rurprocess" },
  ];
  addressInput: string;

  modelsSummary: Record<string, string[]>;
  modelsSummaryLength: number;
  totalCosts: string;
  dataSource = new MatTableDataSource<Tool>([]);

  columns = [
    // ToolId and the serial number are exchanged intentionally
    { key: "serialNumber", title: LocalizationConstants.TOOL_TOOL_ID },
    { key: "toolId", title: LocalizationConstants.TOOL_SERIAL_NUMBER },
    { key: "modelNumber", title: LocalizationConstants.TOOL_MODEL_NUMBER },
    {
      key: "cost",
      title: LocalizationConstants.PURCHASE_ORDER_TOOL_SERVICE_PRICE,
    },
    {
      key: "toolQuote",
      title: LocalizationConstants.PURCHASE_ORDER_TOOL_QUOTE,
    },
  ];

  columnKeysAll = [...this.columns.map((x) => x.key), "action1"];

  enterPOStepperId = "purchase-order-enter-po-step";

  constructor(
    public toolService: ToolService,
    private router: Router,
    public companyService: CompanyService,
    public basket: PoBasketService,
    private eventService: EventService,
    private globalOverlayService: GlobalOverlayService,
    private storage: StorageService,
    private translate: TranslateService,
    private zone: NgZone,
    private modalService: ModalService,
    private el: ElementRef,
    public brandingService: BrandingService,
  ) {
    this.po = new PoSubmit();

    this._subscriptions$.push(this.onBasketChange());
    this.getShipToAsync();
  }

  ngOnInit() {
    this.getQuotes();
  }

  getTools() {
    this.basket.tools = this.basket.getAll();
    this.dataSource.data = [...this.basket.tools];
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  private onBasketChange(): Subscription {
    return this.basket.toolsChange$.subscribe(() => this.getTools());
  }

  /**
   * Create dictionary of serialNumbers from basket.tools grouped by modelNumber
   */
  private createModelsSummary(): void {
    this.modelsSummaryLength = this.basket.tools.length;
    this.modelsSummary = this.basket.tools.reduce((acc, curr) => {
      const { modelNumber, serialNumber } = curr;
      if (!acc[modelNumber]) {
        acc[modelNumber] = [];
      }
      acc[modelNumber].push(serialNumber);
      return acc;
    }, {});
  }

  async getQuotes() {
    if (!this.basket.tools) {
      return;
    }

    const toolDocuments = await Promise.all(
      this.basket.tools.map((tool) => lastValueFrom(this.toolService.getLastQuote(tool))),
    );

    // TODO: add some replace method, instead of
    // directly manipulating basket internal array
    this.basket.tools.forEach((tool, index) => {
      tool.quote = toolDocuments[index];
    });

    this.basket.persistCurrentBasket();
    this.dataSource.data = [...this.basket.tools];
  }

  async getShipToAsync() {
    this.shipToList = await lastValueFrom(
      this.companyService.getShipFromList(this.companyService.selectedCompany.name),
    );

    const shipTo = this.basket.tools?.find((x) => x.shipTo != null)?.shipTo;
    this.currentShipTo = this.shipToList.find((x) => x.name === shipTo);
  }

  public onAddressChange(address: Address) {
    this.po.shipTo = address?.name;
  }

  removeAll() {
    this.basket.cleanBasket();
    this.dataSource.data = [];
  }

  attachDocument(file: FileToUpload) {
    // null is emitted, in case `Cancel` button is pressed in FileDialog
    if (!file) {
      return;
    }

    this.po.document = file;

    const fileMetadata = new FileMetaData();
    fileMetadata.fileName = file.fileName;
    fileMetadata.fileSize = file.fileSize;
    fileMetadata.fileType = file.fileType;
    fileMetadata.lastModifiedTime = file.lastModifiedTime;
    fileMetadata.lastModifiedDate = file.lastModifiedDate;

    this.po.document = fileMetadata;
    this.poUploadedFile = file.fileUpload;
  }

  async onSubmitPoAsync(matStep: MatStep) {
    this.isSubmitPOPressed = true;
    if (this.isAnyControlInvalid(this.enterPOStepperId)) {
      return;
    }

    let canProceed = await this.checkMissingDocsAsync();
    if (!canProceed) {
      return;
    }

    canProceed = await this.checkScrappedToolAsync();
    if (!canProceed) {
      return;
    }

    canProceed = await this.checkReturnUnrepairedAsync();
    if (!canProceed) {
      return;
    }

    // Complete manually instead of formControl validation
    // to disallow jumping to Confirmation step via Stepper header
    matStep.completed = true;

    this.submitPo();
  }

  private async checkMissingDocsAsync(): Promise<boolean> {
    if (this.po.document) {
      return true;
    }

    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(
        this.localizationConstants.PURCHASE_ORDER_MISSING_DOCUMENTS_TITLE,
        [this.localizationConstants.PURCHASE_ORDER_MISSING_DOCUMENTS_MESSAGE],
      ),
    );

    return await lastValueFrom(resultObservable);
  }

  private async checkScrappedToolAsync(): Promise<boolean> {
    if (!this.scrap) {
      return true;
    }

    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(
        this.localizationConstants.PURCHASE_ORDER_MARKING_AS_SCRAPPED_TITLE,
        [this.localizationConstants.PURCHASE_ORDER_MARKING_AS_SCRAPPED_MESSAGE],
      ),
    );

    return await lastValueFrom(resultObservable);
  }

  private async checkReturnUnrepairedAsync(): Promise<boolean> {
    if (!this.rur) {
      return true;
    }

    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(
        this.localizationConstants.PURCHASE_ORDER_MARKING_AS_UNREPAIR_TITLE,
        [this.localizationConstants.PURCHASE_ORDER_MARKING_AS_UNREPAIR_MESSAGE],
      ),
    );

    return await lastValueFrom(resultObservable);
  }

  private submitPo() {
    this.po.tools = this.basket.tools;
    this.po.shipTo = this.currentShipTo?.name;
    this.po.scrap = this.scrap;
    this.po.rur = this.rur;

    this.combineRemarks();

    this.globalOverlayService.show();
    this.eventService
      .submitPurchaseOrder(this.po, this.poUploadedFile)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe(() => {
        this.poResult = true;
        this.basket.cleanBasket();

        this.stepper.next();
      });
  }

  combineRemarks() {
    const quoteNewToolMessage =
      this.processType === ProcessType.ScrapAndQuote
        ? `\n\n${this.translate.instant("purchaseorder.quotenewtool")}\n`
        : "";
    const customAddress = this.po.shipTo === "other" ? `\n\nAddress:\n${this.addressInput}\n` : "";
    const processTypeMessage = this.scrapOrRur ? `\n\nProcess Type: ${this.processTypeTitle}` : "";

    this.po.commentsToShow = this.po.comments;
    this.po.comments = `${
      !this.po.comments ? "\n" : "\n" + this.po.comments
    }${quoteNewToolMessage}${processTypeMessage}${customAddress}`;
  }

  get processTypeTitle() {
    return this.translate.instant(
      this.processTypeList.find((x) => x.key === this.processType).title,
    );
  }

  onMyToolsClick(shouldNavigateToInProgressTools: boolean = true) {
    const filter = shouldNavigateToInProgressTools
      ? this.getFilterInProgressSortedByLastModified()
      : new ToolFilter(this.companyService.selectedCompany.name);

    this.storage.setFilterState(filter, ViewType.ToolsAll);
    this.zone.run(() => this.router.navigate([RouteNameConstants.TOOLS]));
  }

  private getFilterInProgressSortedByLastModified(): ToolFilter {
    const filter = new ToolFilter(this.companyService.selectedCompany.name);
    filter.asc = false;
    filter.sort = "lastModifiedDate";
    filter.statusFilters = [StatusTypes.InProcess];
    const columnFilter = new ColumnFilter("eventStatus");
    columnFilter.values = [StatusTypes.InProcess];
    filter.columnFilters = [columnFilter];

    return filter;
  }

  onRurChange() {
    this.po.scrap = this.po.rur ? false : this.po.scrap;
  }

  onScrapChange() {
    this.po.rur = this.po.scrap ? false : this.po.rur;
  }

  get scrapOrRur() {
    return [ProcessType.Scrap, ProcessType.ScrapAndQuote, ProcessType.Rur].includes(
      this.processType,
    );
  }

  get scrap() {
    return [ProcessType.Scrap, ProcessType.ScrapAndQuote].includes(this.processType);
  }

  get rur() {
    return this.processType == ProcessType.Rur;
  }

  public onStartCheckoutClick(): void {
    const totalCosts = this.basket.tools.reduce((acc, curr) => acc + curr.cost, 0);

    this.createModelsSummary();

    this.totalCosts = totalCosts.toFixed(2);
    this.sidePurchaseOrderStepper.open();
    this.setDetailsStepValidators();
  }

  private setDetailsStepValidators(): void {
    const poTrackingNumber = this.stepEnterPOform.form.get("poNumber");
    if (poTrackingNumber) {
      poTrackingNumber.setValidators([Validators.maxLength(50), Validators.required]);
    }
  }

  public continueStepper(): void {
    this.stepper.next();
  }

  /**
   * Check if any control in form is invalid
   * @param formSelector - selector of form to check for invalid controls
   * @returns true if any control is invalid, false otherwise
   */
  private isAnyControlInvalid(formSelector: string): boolean {
    const firstInvalidControl: HTMLElement = this.el.nativeElement.querySelector(
      "#" + formSelector + " .ng-invalid",
    );
    if (firstInvalidControl) {
      firstInvalidControl.focus();
      return true;
    }
    return false;
  }

  public closeStepper(): void {
    this.sidePurchaseOrderStepper.close();
  }

  public closeAndResetStepper() {
    this.stepper.reset();
    this.router.navigate([RouteNameConstants.ROOT]);
  }

  ngOnDestroy(): void {
    this._subscriptions$.forEach((x) => x.unsubscribe());
  }
}
